import * as React from "react"
import { createPortal } from "react-dom"

interface PopoverProps {
  children: React.ReactNode;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}

interface PopoverContextValue {
  open: boolean;
  setOpen: (open: boolean) => void;
  triggerRef: React.RefObject<HTMLElement | null>;
}

const PopoverContext = React.createContext<PopoverContextValue | undefined>(undefined);

const Popover: React.FC<PopoverProps> = ({ children, open: controlledOpen, onOpenChange }) => {
  const [internalOpen, setInternalOpen] = React.useState(false);
  const triggerRef = React.useRef<HTMLElement>(null);
  
  // Use controlled open state if provided, otherwise use internal state
  const open = controlledOpen !== undefined ? controlledOpen : internalOpen;
  
  const setOpen = React.useCallback((newOpen: boolean) => {
    if (onOpenChange) {
      onOpenChange(newOpen);
    } else {
      setInternalOpen(newOpen);
    }
  }, [onOpenChange]);

  return (
    <PopoverContext.Provider value={{ open, setOpen, triggerRef }}>
      {children}
    </PopoverContext.Provider>
  );
};

const usePopover = () => {
  const context = React.useContext(PopoverContext);
  if (!context) {
    throw new Error("usePopover must be used within Popover");
  }
  return context;
};

interface PopoverTriggerProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  asChild?: boolean;
}

const PopoverTrigger = React.forwardRef<HTMLButtonElement, PopoverTriggerProps>(
  ({ children, onClick, asChild, ...props }, ref) => {
    const { open, setOpen, triggerRef } = usePopover();

    const handleClick = (e: React.MouseEvent<HTMLButtonElement>) => {
      e.preventDefault();
      e.stopPropagation();
      setOpen(!open);
      onClick?.(e);
    };

    const setRefs = React.useCallback((node: HTMLElement | null) => {
      // Set the context ref
      (triggerRef as React.MutableRefObject<HTMLElement | null>).current = node;
      
      // Set the forwarded ref if provided
      if (ref) {
        if (typeof ref === 'function') {
          ref(node as any);
        } else {
          (ref as React.MutableRefObject<HTMLElement | null>).current = node as any;
        }
      }
    }, [ref, triggerRef]);

    if (asChild && React.isValidElement(children)) {
      const childProps = (children as any).props || {};
      return React.cloneElement(children as React.ReactElement<any>, {
        ref: setRefs,
        onClick: (e: React.MouseEvent<HTMLButtonElement>) => {
          handleClick(e);
          childProps.onClick?.(e);
        },
      });
    }

    return (
      <button ref={setRefs} onClick={handleClick} {...props}>
        {children}
      </button>
    );
  }
);
PopoverTrigger.displayName = "PopoverTrigger";

interface PopoverContentProps extends React.HTMLAttributes<HTMLDivElement> {
  align?: 'start' | 'center' | 'end';
}

const PopoverContent = React.forwardRef<HTMLDivElement, PopoverContentProps>(
  ({ className = "", align = "center", children, ...props }, ref) => {
    const { open, setOpen, triggerRef } = usePopover();
    const contentRef = React.useRef<HTMLDivElement>(null);
    const [position, setPosition] = React.useState<{ top: number; left?: number; right?: number } | null>(null);

    // Update position when opened
    React.useEffect(() => {
      if (open) {
        const updatePosition = () => {
          if (!triggerRef.current) return;
          
          const rect = triggerRef.current.getBoundingClientRect();
          const pos: { top: number; left?: number; right?: number } = {
            top: rect.bottom + 8,
          };

          if (align === 'end') {
            pos.right = window.innerWidth - rect.right;
          } else if (align === 'center') {
            pos.left = rect.left + rect.width / 2;
          } else {
            pos.left = rect.left;
          }

          setPosition(pos);
        };

        // Initial position update with a slight delay to ensure DOM is ready
        const timer = setTimeout(updatePosition, 0);
        
        window.addEventListener('scroll', updatePosition);
        window.addEventListener('resize', updatePosition);

        return () => {
          clearTimeout(timer);
          window.removeEventListener('scroll', updatePosition);
          window.removeEventListener('resize', updatePosition);
        };
      } else {
        setPosition(null);
      }
    }, [open, align, triggerRef]);

    React.useEffect(() => {
      const handleClickOutside = (event: MouseEvent) => {
        if (contentRef.current && !contentRef.current.contains(event.target as Node)) {
          // Check if click was on trigger
          if (triggerRef.current && !triggerRef.current.contains(event.target as Node)) {
            setOpen(false);
          }
        }
      };

      if (open) {
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
      }
    }, [open, setOpen, triggerRef]);

    if (!open) return null;

    const alignTransform = align === 'center' ? '-translate-x-1/2' : '';

    const content = (
      <div
        ref={(node) => {
          if (typeof ref === 'function') ref(node);
          else if (ref) ref.current = node;
          (contentRef as React.MutableRefObject<HTMLDivElement | null>).current = node;
        }}
        className={`fixed rounded-md border bg-popover text-popover-foreground shadow-lg outline-none ${alignTransform} ${className}`}
        style={{
          top: position ? `${position.top}px` : '0px',
          left: position?.left !== undefined ? `${position.left}px` : undefined,
          right: position?.right !== undefined ? `${position.right}px` : undefined,
          visibility: position ? 'visible' : 'hidden',
          zIndex: 9999,
          ...props.style,
        }}
        {...props}
      >
        {children}
      </div>
    );

    return createPortal(content, document.body);
  }
);
PopoverContent.displayName = "PopoverContent";

export { Popover, PopoverTrigger, PopoverContent };

